import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

const SECTIONS = [
  { id: 'intro', icon: '👋' },
  { id: 'firstRule', icon: '1️⃣' },
  { id: 'anatomy', icon: '🔬' },
  { id: 'patterns', icon: '🧱' },
  { id: 'concepts', icon: '🧩' },
  { id: 'expressions', icon: '🔗' },
  { id: 'conditions', icon: '⚙️' },
  { id: 'dataFlow', icon: '🌊' },
  { id: 'scoping', icon: '🎯' },
  { id: 'multiLang', icon: '🌐' },
  { id: 'examples', icon: '📝' },
  { id: 'reference', icon: '📖' },
];

function SectionHeading({ icon, title }) {
  return (
    <h2 style={{ fontSize: '1.375rem', fontWeight: 700, marginBottom: '0.75rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
      <span>{icon}</span> {title}
    </h2>
  );
}

function CodeBlock({ code }) {
  return (
    <pre style={{ background: '#1e1e1e', color: '#d4d4d4', padding: '1rem', borderRadius: '0.5rem', fontSize: '0.82rem', fontFamily: "'JetBrains Mono', 'Menlo', monospace", overflowX: 'auto', whiteSpace: 'pre', lineHeight: 1.5 }}>
      <code>{code}</code>
    </pre>
  );
}

function LevelBadge({ level, t }) {
  const config = {
    beginner: { color: 'success', label: t('vigilTutorial.levelBeginner') },
    intermediate: { color: 'warning', label: t('vigilTutorial.levelIntermediate') },
    advanced: { color: 'danger', label: t('vigilTutorial.levelAdvanced') },
  };
  const c = config[level] || config.beginner;
  return <span className={`badge bg-${c.color} me-2`}>{c.label}</span>;
}

function BulletList({ items }) {
  if (!items || !Array.isArray(items)) return null;
  return (
    <ul style={{ lineHeight: 1.8, paddingLeft: '1.25rem', marginBottom: 0 }}>
      {items.map((item, i) => (
        <li key={i}>{item}</li>
      ))}
    </ul>
  );
}

export default function VigilTutorial() {
  const { t } = useTranslation();

  const renderSection = (section) => {
    const base = `vigilTutorial.sections.${section.id}`;
    const body = t(`${base}.body`);
    const level = t(`${base}.level`);

    switch (section.id) {
      case 'intro': {
        const keyPoints = t(`${base}.keyPoints`, { returnObjects: true });
        return (
          <>
            <LevelBadge level={level} t={t} />
            <p style={{ lineHeight: 1.75, marginTop: '0.5rem' }}>{body}</p>
            <BulletList items={keyPoints} />
          </>
        );
      }

      case 'firstRule': {
        const code = t(`${base}.code`);
        const runCode = t(`${base}.runCode`);
        const explanation = t(`${base}.explanation`);
        return (
          <>
            <LevelBadge level={level} t={t} />
            <p style={{ lineHeight: 1.75, marginTop: '0.5rem' }}>{body}</p>
            <CodeBlock code={code} />
            <div className="fw-semibold mt-3 mb-1">Run it:</div>
            <CodeBlock code={runCode} />
            <p className="small text-secondary mt-2" style={{ lineHeight: 1.7 }}>{explanation}</p>
          </>
        );
      }

      case 'anatomy': {
        const fields = t(`${base}.fields`, { returnObjects: true });
        return (
          <>
            <LevelBadge level={level} t={t} />
            <p style={{ lineHeight: 1.75, marginTop: '0.5rem' }}>{body}</p>
            <div className="table-responsive">
              <table className="table table-sm table-bordered" style={{ fontSize: '0.85rem' }}>
                <thead>
                  <tr><th>Field</th><th>Required</th><th>Description</th></tr>
                </thead>
                <tbody>
                  {Array.isArray(fields) && fields.map((f, i) => (
                    <tr key={i}>
                      <td className="font-monospace" style={{ whiteSpace: 'nowrap' }}>{f.name}</td>
                      <td>{f.required ? '✅' : ''}</td>
                      <td>{f.description}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        );
      }

      case 'patterns': {
        const examples = t(`${base}.examples`, { returnObjects: true });
        return (
          <>
            <LevelBadge level={level} t={t} />
            <p style={{ lineHeight: 1.75, marginTop: '0.5rem' }}>{body}</p>
            {Array.isArray(examples) && examples.map((ex, i) => (
              <div key={i} className="mb-3">
                <div className="fw-semibold">{ex.title}</div>
                <div className="small text-secondary mb-1">{ex.description}</div>
                <CodeBlock code={ex.code} />
              </div>
            ))}
          </>
        );
      }

      case 'concepts': {
        const example = t(`${base}.example`);
        const categories = t(`${base}.categories`, { returnObjects: true });
        return (
          <>
            <LevelBadge level={level} t={t} />
            <p style={{ lineHeight: 1.75, marginTop: '0.5rem' }}>{body}</p>
            <CodeBlock code={example} />
            <div className="fw-semibold mt-3 mb-2">Concept categories:</div>
            <BulletList items={categories} />
          </>
        );
      }

      case 'expressions':
      case 'conditions':
      case 'dataFlow':
      case 'scoping': {
        const examples = t(`${base}.examples`, { returnObjects: true });
        return (
          <>
            <LevelBadge level={level} t={t} />
            <p style={{ lineHeight: 1.75, marginTop: '0.5rem' }}>{body}</p>
            {Array.isArray(examples) && examples.map((ex, i) => (
              <div key={i} className="mb-3">
                <div className="fw-semibold">{ex.title}</div>
                <div className="small text-secondary mb-1">{ex.description}</div>
                <CodeBlock code={ex.code} />
              </div>
            ))}
          </>
        );
      }

      case 'multiLang': {
        const example = t(`${base}.example`);
        return (
          <>
            <LevelBadge level={level} t={t} />
            <p style={{ lineHeight: 1.75, marginTop: '0.5rem' }}>{body}</p>
            <CodeBlock code={example} />
          </>
        );
      }

      case 'examples': {
        const rules = t(`${base}.rules`, { returnObjects: true });
        return (
          <>
            <LevelBadge level={level} t={t} />
            <p style={{ lineHeight: 1.75, marginTop: '0.5rem' }}>{body}</p>
            {Array.isArray(rules) && rules.map((rule, i) => (
              <div key={i} className="mb-4">
                <div className="d-flex align-items-center gap-2 mb-1">
                  <div className="fw-semibold">{rule.title}</div>
                  <LevelBadge level={rule.level} t={t} />
                </div>
                <CodeBlock code={rule.code} />
              </div>
            ))}
          </>
        );
      }

      case 'reference': {
        const findTypes = t(`${base}.findTypes`);
        const expressions = t(`${base}.expressions`);
        const conditions = t(`${base}.conditions`);
        return (
          <>
            <LevelBadge level={level} t={t} />
            <p style={{ lineHeight: 1.75, marginTop: '0.5rem' }}>{body}</p>
            <div className="fw-semibold mt-2 mb-1">Find Types</div>
            <CodeBlock code={findTypes} />
            <div className="fw-semibold mt-3 mb-1">Expressions</div>
            <CodeBlock code={expressions} />
            <div className="fw-semibold mt-3 mb-1">Conditions</div>
            <CodeBlock code={conditions} />
          </>
        );
      }

      default:
        return (
          <>
            <LevelBadge level={level} t={t} />
            <p style={{ lineHeight: 1.75, marginTop: '0.5rem' }}>{body}</p>
          </>
        );
    }
  };

  return (
    <div className="App-body">
      <div className="ce-page-header" style={{ paddingLeft: 0, paddingRight: 0, marginBottom: '1.5rem' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '0.75rem' }}>
          <div>
            <h1 className="ce-page-title">📖 {t('vigilTutorial.title')}</h1>
            <p className="ce-page-subtitle">{t('vigilTutorial.subtitle')}</p>
          </div>
          <div className="d-flex gap-2 flex-wrap">
            <Link className="btn btn-outline-secondary btn-sm" to="/docs/vigil">
              {t('vigilTutorial.backToDocs')}
            </Link>
            <Link className="btn btn-outline-primary btn-sm" to="/rule-editor">
              {t('vigilTutorial.openRuleEditor')}
            </Link>
          </div>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '220px 1fr', gap: '2.5rem', alignItems: 'start' }}>
        <div className="docs-layout-toc" style={{ position: 'sticky', top: '1.5rem' }}>
          <div className="section-title" style={{ marginBottom: '0.625rem' }}>{t('vigilTutorial.contents')}</div>
          {SECTIONS.map((section) => (
            <a
              key={section.id}
              href={`#${section.id}`}
              style={{
                display: 'block', padding: '0.3rem 0.625rem', borderRadius: 'var(--radius-xs)',
                fontSize: '0.8125rem', color: 'var(--bs-secondary-color)', textDecoration: 'none', marginBottom: '1px',
              }}
            >
              {section.icon} {t(`vigilTutorial.sections.${section.id}.title`)}
            </a>
          ))}
        </div>

        <div className="docs-layout-content">
          {SECTIONS.map((section) => (
            <section key={section.id} id={section.id} style={{ marginBottom: '2.5rem', scrollMarginTop: '1.5rem' }}>
              <SectionHeading icon={section.icon} title={t(`vigilTutorial.sections.${section.id}.title`)} />
              {renderSection(section)}
            </section>
          ))}
        </div>
      </div>
    </div>
  );
}
